import {
  createElement,
  forwardRef,
  type ComponentPropsWithoutRef,
  type ForwardRefExoticComponent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

type MotionComponent<P> = ForwardRefExoticComponent<P & { children?: ReactNode }>;

type MotionFactory = <T extends keyof JSX.IntrinsicElements>(
  tag: T,
) => MotionComponent<ComponentPropsWithoutRef<T>>;

const createMotionComponent: MotionFactory = (tag) =>
  forwardRef<HTMLElement, ComponentPropsWithoutRef<typeof tag>>((props, ref) =>
    createElement(tag, { ...props, ref }) as React.ReactElement
  ) as MotionComponent<ComponentPropsWithoutRef<typeof tag>>;

export const motion = {
  div: createMotionComponent('div'),
  section: createMotionComponent('section'),
  header: createMotionComponent('header'),
  article: createMotionComponent('article'),
  span: createMotionComponent('span'),
  h2: createMotionComponent('h2'),
  h3: createMotionComponent('h3'),
  p: createMotionComponent('p'),
  button: createMotionComponent('button'),
  svg: createMotionComponent('svg'),
  g: createMotionComponent('g'),
  rect: createMotionComponent('rect'),
  circle: createMotionComponent('circle'),
  path: createMotionComponent('path'),
};

export type MotionValue<T> = {
  get(): T;
  set(next: T): void;
  on(event: 'change', handler: (value: T) => void): () => void;
};

export function useMotionValue<T>(initial: T): MotionValue<T> {
  const [state, setState] = useState(initial);
  const handlersRef = useRef(new Set<(value: T) => void>());

  useEffect(() => {
    handlersRef.current.forEach((handler) => handler(state));
  }, [state]);

  return {
    get: () => state,
    set: (next: T) => setState(next),
    on: (_event, handler) => {
      handlersRef.current.add(handler);
      handler(state);
      return () => handlersRef.current.delete(handler);
    },
  };
}

export function animate<T>(value: MotionValue<T>, next: T) {
  value.set(next);
  return {
    stop: () => undefined,
  };
}

export const AnimatePresence: React.FC<{ children?: ReactNode }> = ({ children }) => <>{children}</>;

export const easeOut = (progress: number) => 1 - (1 - progress) * (1 - progress);

export default {
  motion,
  AnimatePresence,
  useMotionValue,
  animate,
  easeOut,
};
